import { useRef, useState, useEffect } from "react";
import BookCard from "./BookCard";

export default function Carousel({ title, books, onView, cart, addCart, compact = false, renderHeader }) {
    const [page, setPage] = useState(0);
    const visCount = compact ? 5 : 4;
    const pageCount = Math.ceil(books.length / visCount);
    const maxPage = Math.max(0, pageCount - 1);

    const prev = () => setPage(p => Math.max(0, p - 1));
    const next = () => setPage(p => Math.min(maxPage, p + 1));

    // Auto-play functionality
    useEffect(() => {
        // Change slide every 3000ms (3 seconds)
        const interval = setInterval(() => {
            setPage(p => (p < maxPage ? p + 1 : 0)); // Go to next, or loop back to 0
        }, 3000);

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, [maxPage]); // Re-run if maxPage changes

    const trackRef = useRef();
    const cardW = compact ? 180 : 110;
    const gap = 22;
    const batchW = visCount * cardW + (visCount - 1) * gap;
    const totalW = books.length * cardW + (books.length - 1) * gap;
    const maxTranslate = Math.max(0, totalW - batchW);
    const translate = Math.min(page * (batchW + gap), maxTranslate);

    const dots = (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {Array.from({ length: pageCount }, (_, i) => (
                <div
                    key={i}
                    onClick={() => setPage(i)}
                    style={{
                        width: i === page ? 22 : 8,
                        height: 8,
                        borderRadius: 4,
                        background: i === page ? "#C0392B" : "#e8c9c9",
                        cursor: "pointer",
                        transition: "all 0.3s",
                        boxShadow: i === page ? "0 0 8px rgba(192,57,43,0.4)" : "none"
                    }}
                />
            ))}
        </div>
    );

    return (
        <div style={{ position: "relative" }}>

            {/* Header */}
            {renderHeader ? renderHeader(dots) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    {title && <h2 style={{ margin: 0, fontSize: "1.5rem", color: "#8B1A1A" }}>{title}</h2>}
                    {dots}
                </div>
            )}

            {/* Carousel Track */}
            <div style={{ overflow: "hidden" }} ref={trackRef}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "stretch",
                        gap: gap,
                        transition: "transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94)",
                        transform: `translateX(-${translate}px)`
                    }}
                >
                    {books.map(b => (
                        <BookCard
                            key={b._id}
                            book={b}
                            onView={onView}
                            cart={cart}
                            addCart={addCart}
                            compact={compact}
                        />
                    ))}
                </div>
            </div>

            {/* Prev Arrow */}
            {page > 0 && (
                <button
                    onClick={prev}
                    onMouseEnter={e => { e.currentTarget.style.background = "#C0392B"; e.currentTarget.style.color = "white"; e.currentTarget.style.borderColor = "#C0392B"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "var(--paper)"; e.currentTarget.style.color = "inherit"; e.currentTarget.style.borderColor = "#e8c9c9"; }}
                    style={{
                        position: "absolute", left: -22, top: "50%", transform: "translateY(-50%)",
                        width: 44, height: 44, borderRadius: "50%",
                        background: "var(--paper)", border: "1.5px solid #e8c9c9",
                        cursor: "pointer", fontSize: 18,
                        boxShadow: "0 2px 12px rgba(192,57,43,0.15)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 2, transition: "all 0.2s"
                    }}
                >‹</button>
            )}

            {/* Next Arrow */}
            {page < maxPage && (
                <button
                    onClick={next}
                    onMouseEnter={e => { e.currentTarget.style.background = "#C0392B"; e.currentTarget.style.color = "white"; e.currentTarget.style.borderColor = "#C0392B"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "var(--paper)"; e.currentTarget.style.color = "inherit"; e.currentTarget.style.borderColor = "#e8c9c9"; }}
                    style={{
                        position: "absolute", right: -22, top: "50%", transform: "translateY(-50%)",
                        width: 44, height: 44, borderRadius: "50%",
                        background: "var(--paper)", border: "1.5px solid #e8c9c9",
                        cursor: "pointer", fontSize: 18,
                        boxShadow: "0 2px 12px rgba(192,57,43,0.15)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 2, transition: "all 0.2s"
                    }}
                >›</button>
            )}

        </div>
    );
}